/**
 * Canonical CSK theorem and mechanism metadata.
 *
 * This module exposes the stable string-level surface that demos,
 * registries, and adapters need at runtime: theorem codes and names,
 * dual-pair names, SDP names, ICG names, generator metadata, and display
 * mode conversions. The values are typed against the core theorem
 * metadata so downstream apps do not re-declare drifting local unions.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  COMPOSITIONALITY_PRINCIPLE_CODES,
  GENERATOR_CODES,
  NEUTRALIZATION_MODE_DISPLAY_NAMES,
  NEUTRALIZATION_MODE_NAMES,
  NEUTRALIZATION_THEOREM_CODES,
} from "../foundation/types.js";
import type {
  CompositionalityPrincipleCode,
  GeneratorCode,
  GuaranteeMeta,
  NeutralizationModeDisplayName,
  NeutralizationModeName,
  NeutralizationTheoremCode,
} from "../foundation/types.js";
import { SDP_REGISTRY } from "../theorems/compositionality.js";
import {
  DATA_LINEAGE_META,
  INV_CONSISTENCY_META,
  RES_BALANCE_META,
} from "../theorems/dualPairs.js";
import {
  COMPOSE_META,
  DATA_ORIGIN_META,
  DATA_REACH_META,
  INV_ESTABLISH_META,
  INV_PRESERVE_META,
  PERMIT_META,
  PRECOMMIT_META,
  RES_CONSUME_META,
  RES_RELEASE_META,
  SEPARATE_META,
} from "./theorem-meta.js";

export const NEUTRALIZATION_THEOREM_NAMES = [
  "DATA.ORIGIN",
  "DATA.REACH",
  "PERMIT",
  "SEPARATE",
  "PRECOMMIT",
  "RES.CONSUME",
  "RES.RELEASE",
  "COMPOSE",
  "INV.ESTABLISH",
  "INV.PRESERVE",
] as const;

export type NeutralizationTheoremName = (typeof NEUTRALIZATION_THEOREM_NAMES)[number];

export type NeutralizationTheoremMeta = GuaranteeMeta & {
  readonly code: NeutralizationTheoremCode;
  readonly name: NeutralizationTheoremName;
};

/** Literal metadata for one canonical Neutralization Theorem guarantee. */
export type NeutralizationSecurityGuarantee = NeutralizationTheoremMeta;

export const NEUTRALIZATION_THEOREM_META = [
  DATA_ORIGIN_META,
  DATA_REACH_META,
  PERMIT_META,
  SEPARATE_META,
  PRECOMMIT_META,
  RES_CONSUME_META,
  RES_RELEASE_META,
  COMPOSE_META,
  INV_ESTABLISH_META,
  INV_PRESERVE_META,
] as const satisfies readonly NeutralizationTheoremMeta[];

export const DUAL_PAIR_GUARANTEE_NAMES = [
  "DATA.LINEAGE",
  "INV.CONSISTENCY",
  "RES.BALANCE",
] as const;

export type DualPairGuaranteeName = (typeof DUAL_PAIR_GUARANTEE_NAMES)[number];

export const DUAL_PAIR_GUARANTEE_META = [
  DATA_LINEAGE_META,
  INV_CONSISTENCY_META,
  RES_BALANCE_META,
] as const satisfies readonly {
  readonly name: DualPairGuaranteeName;
  readonly mode: NeutralizationModeName;
  readonly leftTheorem: NeutralizationTheoremCode;
  readonly rightTheorem: NeutralizationTheoremCode;
}[];

export type DualPairGuaranteeMeta = (typeof DUAL_PAIR_GUARANTEE_META)[number];

export const COMPOSITIONALITY_PRINCIPLE_NAMES = [
  "TRACE",
  "ALWAYS",
  "ACCOUNT",
  "REQUIRE",
  "DELEGATE",
  "DECOMPOSE",
  "PARTITION",
] as const;

export type CompositionalityPrincipleName = (typeof COMPOSITIONALITY_PRINCIPLE_NAMES)[number];

export const COMPOSITIONALITY_PRINCIPLE_META = SDP_REGISTRY satisfies readonly {
  readonly code: CompositionalityPrincipleCode;
  readonly name: CompositionalityPrincipleName;
  readonly theorems: readonly NeutralizationTheoremCode[];
}[];

export type CompositionalityPrincipleMeta = (typeof COMPOSITIONALITY_PRINCIPLE_META)[number];

export const COMPOSITIONALITY_PRINCIPLE_NAME_BY_CODE = {
  Tr: "TRACE",
  Al: "ALWAYS",
  St: "ACCOUNT",
  At: "REQUIRE",
  Dt: "DELEGATE",
  De: "DECOMPOSE",
  Pa: "PARTITION",
} as const satisfies Record<CompositionalityPrincipleCode, CompositionalityPrincipleName>;

export const COMPOSITIONALITY_PRINCIPLE_CODE_BY_NAME = {
  TRACE: "Tr",
  ALWAYS: "Al",
  ACCOUNT: "St",
  REQUIRE: "At",
  DELEGATE: "Dt",
  DECOMPOSE: "De",
  PARTITION: "Pa",
} as const satisfies Record<CompositionalityPrincipleName, CompositionalityPrincipleCode>;

export const COMPOSITIONALITY_PRINCIPLE_META_BY_CODE = new Map(
  COMPOSITIONALITY_PRINCIPLE_META.map((meta) => [meta.code, meta]),
) as ReadonlyMap<CompositionalityPrincipleCode, CompositionalityPrincipleMeta>;

export const COMPOSITIONALITY_PRINCIPLE_META_BY_NAME = new Map(
  COMPOSITIONALITY_PRINCIPLE_META.map((meta) => [meta.name, meta]),
) as ReadonlyMap<CompositionalityPrincipleName, CompositionalityPrincipleMeta>;

export const IMMUNITY_CLOSURE_GUARANTEE_NAMES = [
  "ICG-Tr",
  "ICG-Al",
  "ICG-St",
  "ICG-At",
  "ICG-Dt",
  "ICG-De",
  "ICG-Pa",
] as const;

export type ImmunityClosureGuaranteeName = (typeof IMMUNITY_CLOSURE_GUARANTEE_NAMES)[number];

export const IMMUNITY_CLOSURE_GUARANTEE_META = [
  {
    name: "ICG-Tr",
    primal: "DATA.LINEAGE",
    dual: "TRACE",
    adjunction: "fibration ⊣ opfibration",
  },
  {
    name: "ICG-Al",
    primal: "INV.CONSISTENCY",
    dual: "ALWAYS",
    adjunction: "F-algebra ⊣ F-coalgebra",
  },
  {
    name: "ICG-St",
    primal: "RES.BALANCE",
    dual: "ACCOUNT",
    adjunction: "(−⊗A) ⊣ (A*⊗−)",
  },
  {
    name: "ICG-At",
    primal: "PRECOMMIT",
    dual: "REQUIRE",
    adjunction: "monad ⊣ comonad",
  },
  {
    name: "ICG-Dt",
    primal: "PERMIT",
    dual: "DELEGATE",
    adjunction: "slice ⊣ coslice",
  },
  {
    name: "ICG-De",
    primal: "COMPOSE",
    dual: "DECOMPOSE",
    adjunction: "operad ⊣ cooperad",
  },
  {
    name: "ICG-Pa",
    primal: "SEPARATE",
    dual: "PARTITION",
    adjunction: "parameterized self-dual",
  },
] as const satisfies readonly {
  readonly name: ImmunityClosureGuaranteeName;
  readonly primal: NeutralizationTheoremName | DualPairGuaranteeName;
  readonly dual: CompositionalityPrincipleName;
  readonly adjunction: string;
}[];

export type ImmunityClosureGuaranteeMeta = (typeof IMMUNITY_CLOSURE_GUARANTEE_META)[number];

export const IMMUNITY_CLOSURE_GUARANTEE_NAME_BY_SDP_CODE = {
  Tr: "ICG-Tr",
  Al: "ICG-Al",
  St: "ICG-St",
  At: "ICG-At",
  Dt: "ICG-Dt",
  De: "ICG-De",
  Pa: "ICG-Pa",
} as const satisfies Record<CompositionalityPrincipleCode, ImmunityClosureGuaranteeName>;

export const IMMUNITY_CLOSURE_GUARANTEE_META_BY_NAME = new Map(
  IMMUNITY_CLOSURE_GUARANTEE_META.map((meta) => [meta.name, meta]),
) as ReadonlyMap<ImmunityClosureGuaranteeName, (typeof IMMUNITY_CLOSURE_GUARANTEE_META)[number]>;

export type StructuralSafetyMechanismName =
  | NeutralizationTheoremName
  | DualPairGuaranteeName
  | CompositionalityPrincipleName
  | ImmunityClosureGuaranteeName;

export type StructuralSafetyMechanismMeta =
  | NeutralizationSecurityGuarantee
  | DualPairGuaranteeMeta
  | CompositionalityPrincipleMeta
  | ImmunityClosureGuaranteeMeta;

export { NEUTRALIZATION_THEOREM_CODES };
export type { NeutralizationTheoremCode };
export { COMPOSITIONALITY_PRINCIPLE_CODES };
export type { CompositionalityPrincipleCode };

export interface CanonicalGeneratorMeta {
  readonly code: GeneratorCode;
  readonly name: string;
  readonly shortName: string;
  readonly dimension: string;
  readonly question: string;
  readonly pattern: string;
  readonly securityPrinciple: string;
}

export const GENERATOR_META = [
  {
    code: "G1",
    name: "Ambient Effect",
    shortName: "Authority",
    dimension: "Authority",
    question: "Who may act?",
    pattern: "Effect without authority",
    securityPrinciple: "No free lunch",
  },
  {
    code: "G2",
    name: "Epistemic Lift",
    shortName: "Knowledge",
    dimension: "Knowledge",
    question: "Who may know?",
    pattern: "Information → authority",
    securityPrinciple: "Knowing ≠ permission",
  },
  {
    code: "G3",
    name: "Time Reversal",
    shortName: "Time",
    dimension: "Time",
    question: "When do effects commit?",
    pattern: "Retroactive justification",
    securityPrinciple: "Future cannot bless past",
  },
  {
    code: "G4",
    name: "Authority Duplication",
    shortName: "Quantity",
    dimension: "Quantity",
    question: "How much is consumed?",
    pattern: "Copying inflates power",
    securityPrinciple: "Authority is scarce",
  },
] as const satisfies readonly CanonicalGeneratorMeta[];

export const NEUTRALIZATION_THEOREM_META_BY_CODE = new Map(
  NEUTRALIZATION_THEOREM_META.map((meta) => [meta.code, meta]),
) as ReadonlyMap<NeutralizationTheoremCode, NeutralizationTheoremMeta>;

export const NEUTRALIZATION_THEOREM_META_BY_NAME = new Map(
  NEUTRALIZATION_THEOREM_META.map((meta) => [meta.name, meta]),
) as ReadonlyMap<NeutralizationTheoremName, NeutralizationTheoremMeta>;

export const GENERATOR_META_BY_CODE = new Map(
  GENERATOR_META.map((meta) => [meta.code, meta]),
) as ReadonlyMap<GeneratorCode, CanonicalGeneratorMeta>;

const theoremCodeSet = new Set<string>(NEUTRALIZATION_THEOREM_CODES);
const theoremNameSet = new Set<string>(NEUTRALIZATION_THEOREM_NAMES);
const generatorCodeSet = new Set<string>(GENERATOR_CODES);
const displayModeSet = new Set<string>(NEUTRALIZATION_MODE_DISPLAY_NAMES);
const modeSet = new Set<string>(NEUTRALIZATION_MODE_NAMES);

export function isNeutralizationTheoremCode(value: unknown): value is NeutralizationTheoremCode {
  return typeof value === "string" && theoremCodeSet.has(value);
}

export function isNeutralizationTheoremName(value: unknown): value is NeutralizationTheoremName {
  return typeof value === "string" && theoremNameSet.has(value);
}

export function isGeneratorCode(value: unknown): value is GeneratorCode {
  return typeof value === "string" && generatorCodeSet.has(value);
}

export function isNeutralizationModeName(value: unknown): value is NeutralizationModeName {
  return typeof value === "string" && modeSet.has(value);
}

export function isNeutralizationModeDisplayName(
  value: unknown,
): value is NeutralizationModeDisplayName {
  return typeof value === "string" && displayModeSet.has(value);
}

export function getNeutralizationTheoremMeta(
  code: NeutralizationTheoremCode,
): NeutralizationSecurityGuarantee {
  const meta = NEUTRALIZATION_THEOREM_META_BY_CODE.get(code);
  if (meta === undefined) {
    throw new Error(`Unknown Neutralization Theorem code: ${code}`);
  }
  return meta;
}

export function getNeutralizationTheoremMetaByName(
  name: NeutralizationTheoremName,
): NeutralizationSecurityGuarantee {
  const meta = NEUTRALIZATION_THEOREM_META_BY_NAME.get(name);
  if (meta === undefined) {
    throw new Error(`Unknown Neutralization Theorem name: ${name}`);
  }
  return meta;
}

export function getCompositionalityPrincipleMeta(
  code: CompositionalityPrincipleCode,
): CompositionalityPrincipleMeta {
  const meta = COMPOSITIONALITY_PRINCIPLE_META_BY_CODE.get(code);
  if (meta === undefined) {
    throw new Error(`Unknown Structural Duality Principle code: ${code}`);
  }
  return meta;
}

export function getImmunityClosureGuaranteeNameForSdp(
  code: CompositionalityPrincipleCode,
): ImmunityClosureGuaranteeName {
  return IMMUNITY_CLOSURE_GUARANTEE_NAME_BY_SDP_CODE[code];
}

export function getImmunityClosureGuaranteeMeta(
  name: ImmunityClosureGuaranteeName,
): ImmunityClosureGuaranteeMeta {
  const meta = IMMUNITY_CLOSURE_GUARANTEE_META_BY_NAME.get(name);
  if (meta === undefined) {
    throw new Error(`Unknown Immunity Closure Guarantee: ${name}`);
  }
  return meta;
}

export function lookupNeutralizationTheoremMeta(
  code: string,
): NeutralizationSecurityGuarantee | undefined {
  return isNeutralizationTheoremCode(code)
    ? NEUTRALIZATION_THEOREM_META_BY_CODE.get(code)
    : undefined;
}

export function lookupNeutralizationTheoremMetaByName(
  name: string,
): NeutralizationSecurityGuarantee | undefined {
  return isNeutralizationTheoremName(name)
    ? NEUTRALIZATION_THEOREM_META_BY_NAME.get(name)
    : undefined;
}

const DISPLAY_MODE_BY_MODE = {
  immune: "IMMUNE",
  contained: "CONTAINED",
  inert: "INERT",
} as const satisfies Record<NeutralizationModeName, NeutralizationModeDisplayName>;

const MODE_BY_DISPLAY_MODE = {
  IMMUNE: "immune",
  CONTAINED: "contained",
  INERT: "inert",
} as const satisfies Record<NeutralizationModeDisplayName, NeutralizationModeName>;

export function toNeutralizationModeDisplay(
  mode: NeutralizationModeName,
): NeutralizationModeDisplayName {
  return DISPLAY_MODE_BY_MODE[mode];
}

export function fromNeutralizationModeDisplay(
  mode: NeutralizationModeDisplayName,
): NeutralizationModeName {
  return MODE_BY_DISPLAY_MODE[mode];
}
